import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Info,
  Radio,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { Badge, type BadgeProps } from './badge';

/** Per-variant status icon (docs/15 §1.2 — status is never color alone: icon + label). */
const STATUS_ICONS: Partial<Record<NonNullable<BadgeProps['variant']>, LucideIcon>> = {
  live: Radio,
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info,
  destructive: XCircle,
  neutral: Circle,
};

/**
 * Status-signalling badge: a `Badge` whose variant-mapped icon is rendered
 * beside the (i18n'd) label, satisfying §1.2's icon-and-label pairing in one
 * place for both SPAs. Use for state/status; plain `Badge` stays for
 * decorative/categorical chips (roles, counts).
 */
export function StatusBadge({ children, className, ...props }: BadgeProps) {
  const Icon = props.variant ? STATUS_ICONS[props.variant] : undefined;
  // `whitespace-nowrap` belongs here, not on the shared `Badge` base: a status pill is
  // a single token — wrapping it ("Devam / ediyor") reads as two states and blows up
  // the row height — whereas a plain `Badge` may hold free text that must wrap.
  return (
    <Badge {...props} className={className ? `gap-1 whitespace-nowrap ${className}` : 'gap-1 whitespace-nowrap'}>
      {Icon && <Icon aria-hidden className="h-3 w-3 shrink-0" />}
      {children}
    </Badge>
  );
}
